import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, TextField, Button, FormControl,
  InputLabel, Select, MenuItem, Chip, IconButton, FormHelperText,
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, Snackbar, Stack, Divider,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import apiClient from '../../services/apiClient';

// Proficiency level options
const proficiencyLevels = [
  { value: 'A0', label: 'A0 (Absolute Beginner)' },
  { value: 'A1', label: 'A1 (Beginner)' },
  { value: 'A2', label: 'A2 (Elementary)' },
  { value: 'B1', label: 'B1 (Intermediate)' },
  { value: 'B2', label: 'B2 (Upper Intermediate)' },
  { value: 'C1', label: 'C1 (Advanced)' },
  { value: 'C2', label: 'C2 (Proficient)' }
];

// Reference language options (for admin reference only)
const referenceLanguages = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Spanish' },
  { code: 'fr', name: 'French' },
  { code: 'de', name: 'German' },
  { code: 'it', name: 'Italian' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'ja', name: 'Japanese' },
  { code: 'ko', name: 'Korean' },
  { code: 'zh', name: 'Chinese' }
];

const PathForm = () => {
  const { pathId } = useParams();
  const navigate = useNavigate();
  const isEditMode = pathId !== 'new' && pathId !== undefined;

  // Form state - NO language_code field
  const [formValues, setFormValues] = useState({
    title: '',
    description: '',
    level: '',
    reference_language: 'en',  // For admin reference only
    create_reference_translation: true,
    tags: []
  });

  // UI state
  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [newTag, setNewTag] = useState('');
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [suggestedTags] = useState([
    'Grammar', 'Vocabulary', 'Conversation', 'Reading', 'Writing',
    'Business', 'Travel', 'Daily Life', 'Culture', 'Beginner Friendly'
  ]);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Load path data if in edit mode
  useEffect(() => {
    if (isEditMode) {
      fetchPathData();
    }
  }, [pathId]);

  // Fetch path data
  const fetchPathData = async () => {
    setLoading(true);
    try {
      const response = await apiClient.admin.getPath(pathId);
      const pathData = response.data.path;

      setFormValues({
        title: pathData.title || '',
        description: pathData.description || '',
        level: pathData.level || '',
        reference_language: pathData.reference_language || 'en',
        tags: pathData.tags || []
      });
    } catch (error) {
      console.error('Error fetching path:', error);
      showNotification('Failed to load path data', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues(prev => ({ ...prev, [name]: value }));

    // Clear error when field is modified
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Handle tag removal
  const handleDeleteTag = (tagToDelete) => {
    setFormValues(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToDelete)
    }));
  };

  // Handle tag addition
  const handleAddTag = () => {
    if (!newTag.trim()) return;

    if (!formValues.tags.includes(newTag)) {
      setFormValues(prev => ({
        ...prev,
        tags: [...prev.tags, newTag]
      }));
    }

    setNewTag('');
    setTagDialogOpen(false);
  };

  // Handle tag dialog
  const openTagDialog = () => {
    setTagDialogOpen(true);
  };

  const closeTagDialog = () => {
    setTagDialogOpen(false);
    setNewTag('');
  };

  // Handle selecting suggested tag
  const handleSelectSuggestedTag = (tag) => {
    if (!formValues.tags.includes(tag)) {
      setFormValues(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
    }
  };

  // Validate form - NO language_code validation
  const validateForm = () => {
    const newErrors = {};

    if (!formValues.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formValues.level) {
      newErrors.level = 'Level is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSaving(true);

    try {
      if (isEditMode) {
        // Update existing path
        await apiClient.admin.updatePath(pathId, formValues);
        showNotification('Path updated successfully', 'success');
      } else {
        // Create new path - NO language_code sent
        const response = await apiClient.admin.createPath(formValues);
        showNotification('Path created successfully', 'success');

        // Navigate to the new path's page to add stories
        navigate(`/admin/paths/${response.data.path.path_id}`);
      }
    } catch (error) {
      console.error('Error saving path:', error);
      showNotification('Failed to save path', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Handle navigation back
  const handleBack = () => {
    navigate('/admin/paths');
  };

  // Show notification
  const showNotification = (message, severity) => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  // Close notification
  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 8 }}>
      {/* Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 3, gap: 1.5 }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <IconButton onClick={handleBack} aria-label="Back">
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>
              {isEditMode ? 'Edit Path' : 'Create New Path'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Paths are language-agnostic — translations are added per story.
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            variant="text"
            onClick={handleBack}
            disabled={saving}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="path-form"
            variant="contained"
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            disabled={saving}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            {saving ? 'Saving…' : isEditMode ? 'Update Path' : 'Create Path'}
          </Button>
        </Stack>
      </Stack>

      <Paper
        component="form"
        id="path-form"
        onSubmit={handleSubmit}
        variant="outlined"
        sx={{ borderRadius: 3, p: { xs: 2, md: 3 } }}
      >
        <Stack spacing={2.5}>
          {/* Details */}
          <Typography variant="overline" color="text.secondary">
            Details
          </Typography>

          <TextField
            fullWidth
            label="Path Title"
            name="title"
            value={formValues.title}
            onChange={handleInputChange}
            error={!!errors.title}
            helperText={errors.title}
            placeholder="E.g., Travel Essentials, Business Communication, etc."
            required
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5}>
            <FormControl fullWidth error={!!errors.level} required>
              <InputLabel id="level-label">Proficiency Level</InputLabel>
              <Select
                labelId="level-label"
                id="level"
                name="level"
                value={formValues.level}
                onChange={handleInputChange}
                label="Proficiency Level"
              >
                {proficiencyLevels.map(level => (
                  <MenuItem key={level.value} value={level.value}>
                    {level.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.level && <FormHelperText>{errors.level}</FormHelperText>}
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="ref-lang-label">Reference Language</InputLabel>
              <Select
                labelId="ref-lang-label"
                name="reference_language"
                value={formValues.reference_language}
                onChange={handleInputChange}
                label="Reference Language"
              >
                {referenceLanguages.map(lang => (
                  <MenuItem key={lang.code} value={lang.code}>
                    {lang.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Used as the base language when creating initial story content
              </FormHelperText>
            </FormControl>
          </Stack>

          <TextField
            fullWidth
            label="Description"
            name="description"
            value={formValues.description}
            onChange={handleInputChange}
            multiline
            rows={4}
            placeholder="Describe what learners will gain from this path"
          />

          <Divider sx={{ borderColor: 'divider' }} />

          {/* Tags */}
          <Box>
            <Typography variant="overline" color="text.secondary">
              Tags
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
              {formValues.tags.map(tag => (
                <Chip
                  key={tag}
                  label={tag}
                  onDelete={() => handleDeleteTag(tag)}
                  color="primary"
                  variant="outlined"
                />
              ))}
              <Chip
                icon={<AddIcon />}
                label="Add Tag"
                onClick={openTagDialog}
                variant="outlined"
                sx={{ cursor: 'pointer' }}
              />
            </Box>
          </Box>

          {isEditMode && (
            <>
              <Divider sx={{ borderColor: 'divider' }} />
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => navigate(`/admin/stories/new?pathId=${pathId}`)}
                sx={{ textTransform: 'none', borderRadius: 2, alignSelf: 'flex-start' }}
              >
                Add Story to Path
              </Button>
            </>
          )}
        </Stack>
      </Paper>

      {/* Tag Dialog */}
      <Dialog
        open={tagDialogOpen}
        onClose={closeTagDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Add Tag</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="New Tag"
            fullWidth
            variant="outlined"
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleAddTag();
              }
            }}
            sx={{ mb: 3 }}
          />

          <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
            Suggested Tags
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {suggestedTags
              .filter(tag => !formValues.tags.includes(tag))
              .map(tag => (
                <Chip
                  key={tag}
                  label={tag}
                  onClick={() => handleSelectSuggestedTag(tag)}
                  color="primary"
                  variant="outlined"
                  clickable
                  sx={{ cursor: 'pointer' }}
                />
              ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeTagDialog} sx={{ textTransform: 'none', borderRadius: 2 }}>
            Cancel
          </Button>
          <Button
            onClick={handleAddTag}
            variant="contained"
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          elevation={6}
          variant="filled"
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PathForm;
